import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { z } from 'zod'
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton'
import { SplitScreenAuthLayout } from '@/components/layout/SplitScreenAuthLayout'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useGoogleSSOEnabled } from '@/hooks/useGoogleSSOEnabled'
import { useNavigationManager } from '@/lib/navigation'
import { getRememberedEmail, setRememberedEmail } from '@/lib/rememberedEmail'
import { routePaths } from '@/lib/router'
import { useAuthStore } from '@/stores/auth'

// Validation schema
const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().optional(),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, login, isLoading, error, clearError } =
    useAuthStore()
  const googleSSOEnabled = useGoogleSSOEnabled()
  const navigationManager = useNavigationManager(navigate, location)
  const [showPassword, setShowPassword] = useState(false)
  const [searchParams] = useSearchParams()
  const emailFromSignup = searchParams.get('email')
  const returnTo = searchParams.get('returnTo')

  // Check for remembered email on mount
  const rememberedEmail = getRememberedEmail()

  const defaultEmail = emailFromSignup
    ? decodeURIComponent(emailFromSignup)
    : (rememberedEmail ?? '')

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: defaultEmail,
      password: '',
      rememberMe: !!rememberedEmail,
    },
  })

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigationManager.redirectAfterLogin(user ?? undefined, returnTo)
    }
  }, [isAuthenticated, user, navigationManager, returnTo])

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError()

      await login({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
      })

      // Save or clear remembered email based on checkbox
      if (data.rememberMe) {
        setRememberedEmail(data.email)
      } else {
        setRememberedEmail(null)
      }

      const authenticatedUser = useAuthStore.getState().user

      navigationManager.redirectAfterLogin(
        authenticatedUser ?? undefined,
        returnTo
      )
    } catch {
      // Error is handled by the auth store
    }
  }

  const handleForgotPassword = () => {
    navigate(routePaths.forgotPassword)
  }

  // Don't render if already authenticated (prevents flash)
  if (isAuthenticated()) {
    return null
  }

  return (
    <SplitScreenAuthLayout>
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-3 text-left">
          <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
            Welcome back
          </h1>
          <p className="text-[14px] leading-[20px] text-muted-foreground">
            Don't have an account?{' '}
            <Link
              to={
                returnTo
                  ? `${routePaths.register}?returnTo=${encodeURIComponent(returnTo)}`
                  : routePaths.register
              }
              className="text-blue-600"
            >
              Create account
            </Link>
          </p>
        </div>

        {/* Google Sign-In */}
        {googleSSOEnabled && (
          <div className="space-y-4">
            <GoogleSignInButton />
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[13px] text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          </div>
        )}

        {/* Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[14px] font-medium text-foreground">
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder=""
                      className="h-9 rounded-full border-border bg-background px-3 py-1 text-[14px] shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-[color,box-shadow]"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password Field */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[14px] font-medium text-foreground">
                    Password
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder=""
                        className="h-9 rounded-full border-border bg-background px-3 py-1 pr-10 text-[14px] shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-[color,box-shadow]"
                        disabled={isLoading}
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Remember Me & Forgot Password Row */}
            <div className="flex items-center justify-between">
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isLoading}
                        className="h-4 w-4"
                      />
                    </FormControl>
                    <label className="text-[14px] font-normal text-foreground">
                      Remember me
                    </label>
                  </div>
                )}
              />

              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleForgotPassword}
                className="text-[14px] text-blue-600 p-0 h-auto font-medium"
                disabled={isLoading}
              >
                Forgot password?
              </Button>
            </div>

            {/* Submit Error */}
            {error && <Alert variant="destructive">{error.message}</Alert>}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </Form>
      </div>
    </SplitScreenAuthLayout>
  )
}
